"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@templates/design-system";
import { useId } from "react";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { IAccordionStandardProps } from "./AccordionStandard.props";

export const AccordionStandard = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  items,
  openBehavior,
  parentClass,
  spacing,
}: IAccordionStandardProps) => {
  const accordionId = useId();
  const isMultiple = openBehavior === "multiple-open";
  const itemValues = items.map((_, index) => `${accordionId}-item-${index}`);
  const initialOpenValues = isMultiple
    ? items.flatMap((item, index) => (item.initiallyOpen === true ? [itemValues[index]] : []))
    : items.reduce<string[]>((openValues, item, index) => {
        if (openValues.length || item.initiallyOpen !== true) {
          return openValues;
        }

        return [itemValues[index]];
      }, []);
  const hasCommonContent = Boolean(eyebrow || heading || description);
  const hasActions = Boolean(buttons?.length);
  const hasIntro = hasCommonContent || hasActions;
  const accordionItems = items.map((item, index) => (
    <AccordionItem
      className="border-b border-foreground/20"
      key={itemValues[index]}
      value={itemValues[index]}
    >
      <AccordionTrigger className="min-h-20 gap-6 py-6 text-left text-2xl/[1.15] font-semibold tracking-[-0.025em] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring [&>svg]:transition-transform [&>svg]:duration-200 data-[state=open]:[&>svg]:rotate-180 motion-reduce:[&>svg]:transition-none motion-reduce:[&>svg]:duration-0">
        <span className="min-w-0 [overflow-wrap:anywhere]">{item.title}</span>
      </AccordionTrigger>
      <AccordionContent className="pb-7 pt-1 text-start md:pb-8" cls="motion-reduce:animate-none">
        <BlockRichText
          className="max-w-[70ch] text-base/[1.55] text-onSurface-500 [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
          content={item.content}
        />
      </AccordionContent>
    </AccordionItem>
  ));

  return (
    <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        {hasIntro ? (
          <div className="flex flex-col gap-8">
            {hasCommonContent ? (
              <BlockCommonContent
                alignment={alignment}
                description={description}
                eyebrow={eyebrow}
                heading={heading}
                headingLevel="h2"
              />
            ) : null}
            {buttons?.length ? <BlockActions actions={buttons} alignment={alignment} /> : null}
          </div>
        ) : null}
        {isMultiple ? (
          <Accordion
            className="w-full border-t border-foreground/20"
            defaultValue={initialOpenValues}
            type="multiple"
          >
            {accordionItems}
          </Accordion>
        ) : (
          <Accordion
            className="w-full border-t border-foreground/20"
            collapsible
            defaultValue={initialOpenValues[0]}
            type="single"
          >
            {accordionItems}
          </Accordion>
        )}
      </div>
    </BlockContainer>
  );
};

AccordionStandard.displayName = "Accordion::AccordionStandard";
